/**
 * The menu bar, over `nav/nav.config.ts`.
 *
 * One rule, and every entry obeys it: **clicking the name opens that page;
 * clicking the chevron opens the menu.** The bar this replaces had eleven
 * names that opened a menu and one that switched workspace instead, which is
 * how a menu bar stops being predictable — you learn the rule, then one name
 * moves the floor.
 *
 * The sections are what you are examining (Diffusion Weighted Imaging,
 * Perfusion) rather than what kind of operation it is (Preprocess,
 * Registration). Splitting by operation left the DWI workflow with no home and
 * scattered its steps across four menus that each also held perfusion's.
 */

import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { groupLeaves, NAV, type NavLeaf, type NavSection } from "@/nav/nav.config";
import { useNavStore } from "@/nav/navStore";
import { toolById } from "@/tools/catalog";
import { useToolSession } from "@/tools/session";
import { USE_CASES } from "@/usecases/catalog";
import { Search } from "lucide-react";
import { dispatchAction } from "@/shell/actions";
import { useActiveTab, useAppStore, useCurrentCase } from "@/store/appStore";

/** What a leaf is called, whether it is still greyed out, and its status badge. */
function leafInfo(leaf: NavLeaf): { label: string; disabled: boolean; hint?: string; badge?: string } {
  if (leaf.toolId) {
    const tool = toolById(leaf.toolId);
    const future = tool?.status === "future";
    return {
      label: leaf.label ?? tool?.label ?? leaf.toolId,
      disabled: future,
      hint: future ? tool.comingSoon : tool?.description,
      badge: future ? "Not yet available" : undefined,
    };
  }
  if (leaf.useCaseId) {
    const useCase = USE_CASES.find((u) => u.id === leaf.useCaseId);
    return {
      label: leaf.label ?? useCase?.title ?? leaf.useCaseId,
      disabled: Boolean(useCase?.unavailable),
      hint: useCase?.unavailable ?? useCase?.outcome,
    };
  }
  return { label: leaf.label ?? leaf.action ?? "", disabled: false };
}

function LeafRow({ leaf, onRun }: { leaf: NavLeaf; onRun: () => void }) {
  const { label, disabled, hint, badge } = leafInfo(leaf);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={hint}
      onClick={onRun}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs",
        disabled ? "cursor-not-allowed text-ads-faint" : "text-ads-text hover:bg-ads-accent-soft",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge ? (
        <span className="shrink-0 rounded bg-ads-bg px-1 py-px text-[9px] font-medium uppercase tracking-wide text-ads-faint">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function Flyout({ label, leaves, onRun }: { label: string; leaves: NavLeaf[]; onRun: (l: NavLeaf) => void }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-ads-text hover:bg-ads-accent-soft"
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronRight className="h-3 w-3 shrink-0 text-ads-faint" />
      </button>
      {open ? (
        <div className="absolute left-full top-0 z-50 -ml-1 min-w-[15rem] rounded-md border border-ads-border bg-ads-surface p-1 shadow-lg">
          {leaves.map((leaf, i) => (
            <LeafRow key={`${leaf.toolId ?? leaf.useCaseId ?? leaf.action}-${i}`} leaf={leaf} onRun={() => onRun(leaf)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SectionMenu({ section, onClose }: { section: NavSection; onClose: () => void }) {
  const activateLeaf = useNavStore((s) => s.activateLeaf);
  const run = (leaf: NavLeaf) => {
    onClose();
    // Tools is the standalone-run menu: every leaf opens its own popup
    // directly (`ToolLaunchDialog`, then `InteractiveSegPanel` or
    // `StageRunPanel`) instead of `activateLeaf`'s usual route for a tool —
    // jumping to that stage in the open case's pipeline, or a confirm sheet
    // first. The same tool reached from the DWI/Perfusion menus still takes
    // that route unchanged; only this menu means "run this, here, now."
    if (section.id === "tools" && leaf.toolId) {
      useToolSession.getState().selectTool(leaf.toolId);
      useToolSession.getState().openLaunch();
      return;
    }
    activateLeaf(leaf);
  };
  return (
    <div
      role="menu"
      className="absolute left-0 top-full z-50 mt-0.5 max-h-[80vh] min-w-[17rem] overflow-y-auto rounded-md border border-ads-border bg-ads-surface p-1 shadow-lg"
    >
      {section.full ? (
        <p className="px-2 pt-0.5 text-[11px] font-medium text-ads-text">{section.full}</p>
      ) : null}
      {section.blurb ? (
        <p className="px-2 pb-1 pt-0.5 text-[11px] leading-snug text-ads-faint">{section.blurb}</p>
      ) : null}
      {section.groups.map((group, gi) => (
        <div key={group.id}>
          {gi > 0 ? <div className="my-1 h-px bg-ads-border" /> : null}
          <p className="px-2 pt-0.5 text-[10.5px] font-medium uppercase tracking-wide text-ads-faint">
            {group.label}
          </p>
          {group.hint ? (
            <p className="px-2 pb-0.5 text-[10.5px] leading-snug text-ads-faint">{group.hint}</p>
          ) : null}
          {groupLeaves(group).map((bucket, bi) =>
            bucket.submenu ? (
              <Flyout key={bucket.submenu} label={bucket.submenu} leaves={bucket.items} onRun={run} />
            ) : (
              bucket.items.map((leaf, i) => (
                <LeafRow
                  key={`${group.id}-${bi}-${i}`}
                  leaf={leaf}
                  onRun={() => run(leaf)}
                />
              ))
            ),
          )}
        </div>
      ))}
    </div>
  );
}

export function MenuBar() {
  const [openId, setOpenId] = React.useState<string | null>(null);
  const activeTab = useActiveTab();
  const setActiveTab = useAppStore((s) => s.setActiveTab);
  const current = useCurrentCase();

  React.useEffect(() => {
    if (!openId) return;
    const dismiss = () => setOpenId(null);
    document.addEventListener("click", dismiss);
    return () => document.removeEventListener("click", dismiss);
  }, [openId]);

  return (
    <nav
      aria-label="Main menu"
      className="flex h-9 shrink-0 items-stretch gap-0.5 border-b border-ads-border bg-ads-surface px-2"
      onClick={(event) => event.stopPropagation()}
    >
      {NAV.map((section) => {
        const isActive = section.tab != null && section.tab === activeTab;
        return (
          <div key={section.id} className="relative flex items-stretch">
            {/* The name goes to the page. */}
            <button
              type="button"
              aria-current={isActive ? "page" : undefined}
              onClick={() => {
                setOpenId(null);
                if (section.tab) setActiveTab(section.tab);
                else setOpenId(section.id);
              }}
              title={section.full ?? section.label}
              className={cn(
                "flex items-center rounded-l px-2 text-xs font-medium",
                isActive ? "text-ads-accent" : "text-ads-text hover:bg-ads-hover",
              )}
            >
              {section.label}
            </button>
            {/* The chevron opens the menu. */}
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={openId === section.id}
              aria-label={`${section.label} menu`}
              onClick={() => setOpenId((cur) => (cur === section.id ? null : section.id))}
              className={cn(
                "flex items-center rounded-r px-1 text-ads-faint hover:bg-ads-hover hover:text-ads-text",
                openId === section.id && "bg-ads-hover text-ads-text",
              )}
            >
              <ChevronDown className="h-3 w-3" />
            </button>
            {openId === section.id ? (
              <SectionMenu section={section} onClose={() => setOpenId(null)} />
            ) : null}
          </div>
        );
      })}

      {/* The bar is the navigation now, so what used to sit in the workspace
          strip below it — which study is open, and the command search — lives
          here rather than in a second row that repeated these seven names. */}
      <span className="ml-3 flex min-w-0 flex-1 items-center truncate text-[11px] text-ads-muted">
        {current ? `${current.subjectId} · ${current.pipelineKind.toUpperCase()}` : "No study open"}
      </span>
      <button
        type="button"
        onClick={() => dispatchAction("shell.commandPalette")}
        title="Search commands (⌘/Ctrl+Shift+P)"
        className="my-1 flex shrink-0 items-center gap-1.5 rounded border border-ads-border px-2 text-[11px] text-ads-muted hover:bg-ads-hover"
      >
        <Search className="h-3 w-3" />
        Commands
      </button>
    </nav>
  );
}
